import React, { useState, useCallback, useEffect } from 'react';
import { FormField, NumberInput, InputAdornment, RadioButton } from '@cian/ui-kit';
import { useFormContext } from 'react-hook-form';
import cx from 'clsx';

import styles from './styles.css';
import { IField, IFormState } from '../../types/calculator';
import { useBaseField } from '../BaseField';
import { useComponentLayout } from '../../hooks';
import { getFieldPlaceholder } from '../../utils';

export const InputWithOptions: React.FC<IField> = props => {
  const { calculateForField } = props;
  const {
    field: { onChange, name, value: fieldValue, ref },
    fieldState,
  } = useBaseField(props);
  const { watch } = useFormContext<IFormState>();
  const formState = watch();
  const layout = useComponentLayout();
  const [value, setValue] = useState<number | undefined>(undefined);

  const handleChangeOption = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(e.currentTarget.value);
      const resultValueForInput = calculateForField ? calculateForField(value, formState) : value;

      setValue(resultValueForInput);
      onChange(resultValueForInput);
    },
    [calculateForField, formState, onChange],
  );

  useEffect(() => {
    if (!fieldValue) {
      setValue(undefined);
    }
    if (Number(fieldValue)) {
      setValue(Number(fieldValue));
    }
  }, [fieldValue]);

  const handleChangeInput = useCallback(
    async (inputValue: number | undefined) => {
      const value = inputValue ? inputValue : undefined;

      setValue(value);
      await onChange(value);
    },
    [onChange],
  );

  if (!props.source) {
    return null;
  }

  const isError = fieldState.isDirty || fieldState.invalid;
  const isAllChipsHidden = props.source.filter(({ hidden }) => hidden).length === props.source.length;
  const isChecked = (optionValue: string, optionChecked: boolean | undefined) =>
    optionChecked === undefined ? Number(optionValue) === value : optionChecked;

  return (
    <>
      <FormField
        label={props.label}
        errorMessage={isError ? fieldState.error : undefined}
        required={!!props.validation?.required}
        withoutMargin
      >
        <NumberInput
          data-testid={name}
          value={Number(value)}
          onChange={handleChangeInput}
          placeholder={getFieldPlaceholder(props.placeholder)}
          invalid={Boolean(fieldState.isDirty && fieldState.error)}
          disabled={props.disabled}
          name={name}
          ref={ref}
          rightAdornment={<InputAdornment>{props.rightAdornment}</InputAdornment>}
          decimalDigits={props.decimalDigits}
          size="M"
          min={0}
        />
      </FormField>
      {!isAllChipsHidden && (
        <div className={cx(styles.chips, styles[`chips_${layout}`])}>
          {props.source.map(option => (
            <div key={option.value} className={styles.chip}>
              <RadioButton
                data-testid={`${props.name} ${option.value}`}
                label={option.label}
                name={props.name}
                value={option.value}
                onChange={handleChangeOption}
                disabled={option.disabled}
                hidden={option.hidden}
                checked={isChecked(option.value, option.checked)}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

InputWithOptions.displayName = 'InputWithOptions';
